import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BedDouble, Bath, Users, Droplets, MapPin } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import YachtGallery from "@/components/YachtGallery";
import SEO from "@/components/SEO";
import { residences } from "@/data/residences";

const ease = [0.22, 1, 0.36, 1] as const;

const ResidenceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const residence = residences.find((r) => r.slug === slug);

  if (!residence) return <Navigate to="/residences" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title={`${residence.name} — ${residence.location} | Arzani Residences`}
        description={(residence.description?.split("\n\n")[0] || `${residence.name} in ${residence.location}.`).slice(0, 155)}
        path={`/residences/${residence.slug}`}
        image={residence.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Accommodation",
          name: residence.name,
          description: residence.description,
          image: residence.images,
          address: residence.location,
        }}
      />
      <Navigation />

      {/* Hero Image */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <YachtGallery images={residence.images} name={residence.name} />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-foreground/30 pointer-events-none" />
        </div>
        <div className="relative z-10 container-luxury pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1.2, ease }}
          >
            <Link
              to="/residences"
              className="inline-flex items-center gap-2 font-label text-primary-foreground/50 hover:text-primary-foreground transition-colors duration-500 mb-6"
            >
              <ArrowLeft size={14} />
              Back to residences
            </Link>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wide uppercase mb-2">
              {residence.name}
            </h1>
            <p className="font-accent text-primary-foreground/60 text-lg md:text-xl italic">
              {residence.location}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Details */}
      <section className="section-spacing bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-28">
            {/* Left: Description */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease }}
            >
              <span className="font-label text-bronze block mb-6">About This Residence</span>
              {residence.description.split("\n\n").map((paragraph, i) => (
                <p key={i} className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-6">
                  {paragraph}
                </p>
              ))}

              {/* Specs Grid */}
              <div className="grid grid-cols-2 gap-6 mb-10 mt-10">
                <div className="flex items-start gap-3">
                  <BedDouble size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Bedrooms</span>
                    <span className="font-body text-foreground text-sm">{residence.bedrooms}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Bath size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Bathrooms</span>
                    <span className="font-body text-foreground text-sm">{residence.bathrooms}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Guests</span>
                    <span className="font-body text-foreground text-sm">{residence.guests}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Droplets size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Swimming Pools</span>
                    <span className="font-body text-foreground text-sm">{residence.pools}</span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-8">
                <span className="font-label text-foreground/40 block text-[10px] mb-4">Features & Amenities</span>
                <div className="flex flex-wrap gap-3">
                  {residence.features.map((feature) => (
                    <span
                      key={feature}
                      className="font-body text-foreground text-xs border border-border/30 px-4 py-2"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* Highlights */}
              <div>
                <span className="font-label text-foreground/40 block text-[10px] mb-4">Highlights</span>
                <ul className="space-y-2">
                  {residence.highlights.map((h) => (
                    <li key={h} className="font-body text-muted-foreground text-sm flex items-start gap-2">
                      <span className="text-bronze mt-1">·</span>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Right: Pricing + Distances */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.15, ease }}
            >
              <span className="font-label text-bronze block mb-6">Pricing · Season 2026</span>

              {/* Pricing Table */}
              <div className="border border-border/30 mb-10">
                <div className="grid grid-cols-2 border-b border-border/30">
                  <div className="p-5 border-r border-border/30 text-center">
                    <span className="font-label text-foreground/40 block text-[10px] mb-2">Low Season</span>
                    <span className="font-body text-muted-foreground text-xs block mb-1">Rest of the year</span>
                    <span className="font-display-medium text-foreground text-xl md:text-2xl">{residence.pricing.low}</span>
                    <span className="font-body text-muted-foreground text-xs block mt-1">/week</span>
                  </div>
                  <div className="p-5 text-center">
                    <span className="font-label text-foreground/40 block text-[10px] mb-2">High Season</span>
                    <span className="font-body text-muted-foreground text-xs block mb-1">July / August</span>
                    <span className="font-display-medium text-foreground text-xl md:text-2xl">{residence.pricing.high}</span>
                    <span className="font-body text-muted-foreground text-xs block mt-1">/week</span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-body text-muted-foreground/60 text-xs italic text-center">
                    *{residence.priceNote}
                  </p>
                </div>
              </div>

              {/* Distances */}
              <div className="mb-12">
                <span className="font-label text-foreground/40 block text-[10px] mb-4">Distances</span>
                <div className="space-y-3">
                  {residence.distances.map((d) => (
                    <div key={d.place} className="flex items-center justify-between border-b border-border/10 pb-3">
                      <span className="font-body text-foreground text-sm flex items-center gap-2">
                        <MapPin size={14} className="text-bronze shrink-0" />
                        {d.place}
                      </span>
                      <span className="font-body text-muted-foreground text-sm">{d.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="border-t border-border/20 pt-10">
                <h3 className="font-display-medium text-lg text-foreground tracking-wide uppercase mb-4">
                  Interested in this residence?
                </h3>
                <p className="font-body text-muted-foreground text-sm leading-[1.8] mb-6">
                  Send us your preferred dates, group size and any special requests. 
                  We'll confirm availability and arrange every detail.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500 text-center"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`mailto:info@arzanigroupworldwide.com?subject=Residence Inquiry: ${residence.name}`}
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500 text-center"
                  >
                    Email Us
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Browse more */}
      <section className="py-16 bg-background">
        <div className="container-luxury text-center">
          <Link
            to="/residences"
            className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
          >
            Browse All Residences
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResidenceDetail;
